import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Pencil,
  Trash2,
  Plus,
  Check,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  ChevronUp,
} from 'lucide-react';
import type { Statement } from '../types';
import {
  useStatementTransactions,
  usePatchBalances,
  useAddManualTransaction,
  useEditTransaction,
  useDeleteGapTransaction,
  useCloseGap,
  useCleanDuplicates,
  type GapTxn,
} from '../hooks/useStatements';
import { formatAccountName } from '../utils/accounts';
import { Button, EmptyState, IconButton, Input, SkeletonTable, Tag } from './ui';
import { money } from '../lib/money';

interface FixGapPanelProps {
  statement: Statement;
  caseId: string;
  onClose: () => void;
}

function fmt(v: number | null | undefined): string {
  return money(v);
}

function fmtDate(d: string | null): string {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${m}/${day}/${y}`;
}

function computeGap(
  beginning: number | null,
  ending: number | null,
  txns: GapTxn[],
  accountType: string,
): { txnSum: number; expectedEnd: number; gap: number } | null {
  if (beginning === null || ending === null) return null;
  const txnSum = txns
    .filter(t => t.transaction_type !== 'shop_with_points')
    .reduce((s, t) => s + (t.amount ?? 0), 0);
  const isCC = accountType?.startsWith('Credit Card');
  const expectedEnd = isCC ? beginning - txnSum : beginning + txnSum;
  const gap = ending - expectedEnd;
  return { txnSum, expectedEnd, gap };
}

function GapDisplay({ gap, large = false }: { gap: number; large?: boolean }) {
  const abs = Math.abs(gap);
  const isBalanced = abs <= 0.01;
  const isMinor = abs <= 1.0;
  const color = isBalanced
    ? 'text-green-700'
    : isMinor
    ? 'text-clay-700'
    : gap > 0
    ? 'text-info'
    : 'text-danger';
  const prefix = gap > 0.01 ? '+' : '';
  return (
    <span className={`${color} tabular-nums ${large ? 't-num-hero' : 'text-base font-semibold'}`}>
      {isBalanced ? (
        <span className="inline-flex items-center gap-1">$0.00 <Check size={16} className="inline text-green-600" /></span>
      ) : `${prefix}${fmt(gap)}`}
    </span>
  );
}

function InlineBalanceEdit({
  label,
  value,
  onSave,
  isSaving,
}: {
  label: string;
  value: number | null;
  onSave: (v: number) => void;
  isSaving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  function startEdit() {
    setRaw(value !== null ? String(value) : '');
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }

  function save() {
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) {
      onSave(parsed);
    }
    setEditing(false);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') setEditing(false);
  }

  return (
    <div className="flex flex-col">
      <span className="mb-1 text-xs text-ink-faint">{label}</span>
      {editing ? (
        <Input
          ref={inputRef}
          type="number"
          step="0.01"
          size="sm"
          leading="$"
          value={raw}
          onChange={e => setRaw(e.target.value)}
          onKeyDown={onKey}
          onBlur={save}
          className="w-28 text-right"
        />
      ) : (
        /* eslint-disable-next-line no-restricted-syntax -- click-to-edit balance: no ui primitive fits this inline layout */
        <button
          onClick={startEdit}
          disabled={isSaving}
          className="flex items-center gap-1 text-left text-sm text-green-900 tabular-nums hover:text-green-700 hover:underline"
          title="Click to edit"
        >
          {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Pencil size={12} className="text-ink-faint" />}
          {fmt(value)}
        </button>
      )}
    </div>
  );
}

function TxnRow({
  txn,
  onEdit,
  onDelete,
  isEditing,
  isDeleting,
}: {
  txn: GapTxn;
  onEdit: (id: number, statementId: number, field: 'amount' | 'description', value: string) => void;
  onDelete: (id: number, statementId: number) => void;
  isEditing: boolean;
  isDeleting: boolean;
}) {
  const [editField, setEditField] = useState<'amount' | 'description' | null>(null);
  const [rawAmount, setRawAmount] = useState('');
  const [rawDesc, setRawDesc] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);
  const amtRef = useRef<HTMLInputElement>(null);
  const descRef = useRef<HTMLInputElement>(null);

  function startAmountEdit() {
    setRawAmount(txn.amount !== null ? String(txn.amount) : '');
    setEditField('amount');
    setTimeout(() => amtRef.current?.select(), 0);
  }

  function startDescEdit() {
    setRawDesc(txn.description || '');
    setEditField('description');
    setTimeout(() => descRef.current?.select(), 0);
  }

  function saveAmount() {
    const parsed = parseFloat(rawAmount);
    if (!isNaN(parsed) && parsed !== txn.amount) {
      onEdit(txn.id, txn.statement_id, 'amount', String(parsed));
    }
    setEditField(null);
  }

  function saveDesc() {
    const trimmed = rawDesc.trim();
    if (trimmed && trimmed !== txn.description) {
      onEdit(txn.id, txn.statement_id, 'description', trimmed);
    }
    setEditField(null);
  }

  function onAmtKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') saveAmount();
    if (e.key === 'Escape') setEditField(null);
  }

  function onDescKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') saveDesc();
    if (e.key === 'Escape') setEditField(null);
  }

  const isPositive = (txn.amount ?? 0) >= 0;

  return (
    <div className={`flex items-center gap-2 px-3 py-1 text-xs ${txn.manual_override ? 'bg-info-soft' : 'hover:bg-cream'}`}>
      <span className="w-20 shrink-0 text-ink-faint tabular-nums">{fmtDate(txn.date)}</span>

      <div className="min-w-0 flex-1">
        {editField === 'description' ? (
          <Input
            ref={descRef}
            type="text"
            size="sm"
            value={rawDesc}
            onChange={e => setRawDesc(e.target.value)}
            onKeyDown={onDescKey}
            onBlur={saveDesc}
            className="h-6 px-1 text-xs"
          />
        ) : (
          /* eslint-disable-next-line no-restricted-syntax -- click-to-edit description: no ui primitive fits this inline layout */
          <button
            onClick={startDescEdit}
            disabled={isEditing || isDeleting}
            className="max-w-full truncate text-left text-ink-soft hover:text-green-800 hover:underline"
            title={txn.description || ''}
          >
            {txn.description || '—'}
          </button>
        )}
      </div>

      <div className="w-28 shrink-0 text-right">
        {editField === 'amount' ? (
          <Input
            ref={amtRef}
            type="number"
            step="0.01"
            size="sm"
            value={rawAmount}
            onChange={e => setRawAmount(e.target.value)}
            onKeyDown={onAmtKey}
            onBlur={saveAmount}
            className="h-6 px-1 text-right text-xs"
          />
        ) : (
          /* eslint-disable-next-line no-restricted-syntax -- click-to-edit amount: no ui primitive fits this inline layout */
          <button
            onClick={startAmountEdit}
            disabled={isEditing || isDeleting}
            className={`ml-auto flex items-center gap-1 tabular-nums hover:underline ${isPositive ? 'text-ink' : 'text-danger'}`}
            title="Click to edit amount"
          >
            {isEditing ? <Loader2 size={12} className="animate-spin" /> : <Pencil size={12} className="text-ink-faint" />}
            {fmt(txn.amount)}
          </button>
        )}
      </div>

      <div className="shrink-0">
        {confirmDel ? (
          <div className="flex items-center gap-1">
            <IconButton
              icon={Check}
              size="xs"
              variant="danger-ghost"
              aria-label="Confirm delete"
              onClick={() => { setConfirmDel(false); onDelete(txn.id, txn.statement_id); }}
            />
            <IconButton
              icon={X}
              size="xs"
              variant="ghost"
              aria-label="Cancel"
              className="text-ink-faint hover:text-ink-soft"
              onClick={() => setConfirmDel(false)}
            />
          </div>
        ) : (
          <IconButton
            icon={isDeleting ? Loader2 : Trash2}
            size="xs"
            variant="ghost"
            aria-label="Delete transaction"
            disabled={isDeleting || isEditing}
            className={`text-ink-faint hover:text-danger ${isDeleting ? 'animate-spin' : ''}`}
            onClick={() => setConfirmDel(true)}
          />
        )}
      </div>
    </div>
  );
}

export default function FixGapPanel({ statement, caseId, onClose }: FixGapPanelProps) {
  const { data: txns, isLoading: txnsLoading } = useStatementTransactions(statement.id);
  const patchBalances = usePatchBalances(caseId);
  const addTxn = useAddManualTransaction(caseId);
  const editTxn = useEditTransaction(caseId);
  const deleteTxn = useDeleteGapTransaction(caseId);
  const closeGap = useCloseGap(caseId);
  const cleanDuplicates = useCleanDuplicates(caseId);

  const [localBegin, setLocalBegin] = useState<number | null>(statement.beginning_balance);
  const [localEnd, setLocalEnd] = useState<number | null>(statement.ending_balance);
  const [localTxns, setLocalTxns] = useState<GapTxn[]>([]);
  const [editingTxnId, setEditingTxnId] = useState<number | null>(null);
  const [deletingTxnId, setDeletingTxnId] = useState<number | null>(null);
  const [cleanResult, setCleanResult] = useState<{ deleted: number; message: string } | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [addDate, setAddDate] = useState('');
  const [addDesc, setAddDesc] = useState('');
  const [addAmt, setAddAmt] = useState('');
  const [addSign, setAddSign] = useState<'+' | '-'>('-');

  const [closeNote, setCloseNote] = useState('');
  const [showClosePrompt, setShowClosePrompt] = useState(false);

  useEffect(() => {
    if (txns) setLocalTxns(txns);
  }, [txns]);

  useEffect(() => {
    setLocalBegin(statement.beginning_balance);
    setLocalEnd(statement.ending_balance);
  }, [statement.beginning_balance, statement.ending_balance]);

  const live = computeGap(localBegin, localEnd, localTxns, statement.account_type);
  const gapAbs = live ? Math.abs(live.gap) : Infinity;
  const canMarkBalanced = live !== null && gapAbs <= 1.0;

  // Detect dual-extraction pattern: some transactions have dates, others don't.
  // This indicates the consensus merge left behind undated vision-extraction
  // duplicates alongside dated text-extraction transactions.
  const hasDatedTxns = localTxns.some(t => t.date !== null);
  const hasUndatedTxns = localTxns.some(t => t.date === null);
  const hasMixedDates = hasDatedTxns && hasUndatedTxns && !cleanResult;

  function handleCleanDuplicates() {
    setCleanResult(null);
    cleanDuplicates.mutate(
      { statementId: statement.id },
      {
        onSuccess: (data) => {
          setCleanResult({ deleted: data.deleted, message: data.message });
          // React Query invalidation in the hook will refetch the txns list
        },
      }
    );
  }

  function handleSaveBalance(field: 'beginning' | 'ending', value: number) {
    const payload: { statementId: number; beginning_balance?: number; ending_balance?: number } = { statementId: statement.id };
    if (field === 'beginning') {
      payload.beginning_balance = value;
      setLocalBegin(value);
    } else {
      payload.ending_balance = value;
      setLocalEnd(value);
    }
    patchBalances.mutate(payload, {
      onSuccess: (data) => {
        if (data.beginning_balance !== undefined) setLocalBegin(data.beginning_balance);
        if (data.ending_balance !== undefined) setLocalEnd(data.ending_balance);
      },
    });
  }

  function handleEditTxn(id: number, statementId: number, field: 'amount' | 'description', value: string) {
    setEditingTxnId(id);
    const payload: { transactionId: number; statementId: number; amount?: number; description?: string } = { transactionId: id, statementId };
    if (field === 'amount') payload.amount = parseFloat(value);
    if (field === 'description') payload.description = value;

    setLocalTxns(prev => prev.map(t => {
      if (t.id !== id) return t;
      const updated = { ...t };
      if (field === 'amount') updated.amount = parseFloat(value);
      if (field === 'description') updated.description = value;
      return updated;
    }));

    editTxn.mutate(payload, {
      onSuccess: (data) => {
        setLocalTxns(prev => prev.map(t => t.id === data.transaction.id ? data.transaction : t));
        setEditingTxnId(null);
      },
      onError: () => {
        if (txns) setLocalTxns(txns);
        setEditingTxnId(null);
      },
    });
  }

  function handleDeleteTxn(id: number, statementId: number) {
    setDeletingTxnId(id);
    setLocalTxns(prev => prev.filter(t => t.id !== id));
    deleteTxn.mutate({ transactionId: id, statementId }, {
      onError: () => {
        if (txns) setLocalTxns(txns);
        setDeletingTxnId(null);
      },
      onSettled: () => setDeletingTxnId(null),
    });
  }

  function handleAddTxn() {
    if (!addDate || !addDesc.trim() || !addAmt) return;
    const amtVal = parseFloat(addAmt);
    if (isNaN(amtVal)) return;
    const signed = addSign === '-' ? -Math.abs(amtVal) : Math.abs(amtVal);

    const tempId = -(Date.now());
    const optimisticTxn: GapTxn = {
      id: tempId,
      statement_id: statement.id,
      date: addDate,
      description: addDesc.trim(),
      amount: signed,
      transaction_type: signed < 0 ? 'debit' : 'credit',
      property_status: null,
      manual_override: true,
      override_reason: null,
    };
    setLocalTxns(prev => [...prev, optimisticTxn]);
    setShowAddForm(false);
    setAddDate('');
    setAddDesc('');
    setAddAmt('');
    setAddSign('-');

    addTxn.mutate(
      { statementId: statement.id, date: addDate, description: optimisticTxn.description!, amount: signed },
      {
        onSuccess: (data) => {
          setLocalTxns(prev => prev.map(t => t.id === tempId ? data.transaction : t));
        },
        onError: () => {
          setLocalTxns(prev => prev.filter(t => t.id !== tempId));
        },
      }
    );
  }

  function handleCloseGap() {
    if (!closeNote.trim()) return;
    closeGap.mutate(
      { statementId: statement.id, note: closeNote.trim() },
      {
        onSuccess: () => {
          setShowClosePrompt(false);
          onClose();
        },
      }
    );
  }

  const periodStr = [statement.statement_start_date, statement.statement_end_date]
    .filter(Boolean)
    .map(d => new Date(d!).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }))
    .join(' – ');

  const isCC = statement.account_type?.startsWith('Credit Card');

  return (
    <div className="fixed inset-0 z-(--z-modal) flex justify-end">
      <div className="absolute inset-0 bg-ink/40 animate-scrim" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-xl flex-col overflow-hidden rounded-l-overlay border-l border-border bg-surface-card shadow-modal animate-drawer">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between bg-green-800 px-5 py-4 text-white">
          <div>
            <h2 className="t-card text-white">Fix Gap</h2>
            <p className="mt-1 text-xs text-green-200">{formatAccountName(statement.bank_label)} · {periodStr || statement.source_file}</p>
          </div>
          <IconButton
            icon={X}
            aria-label="Close"
            variant="ghost"
            onClick={onClose}
            className="text-green-200 hover:bg-green-700 hover:text-white"
          />
        </div>

        {/* Math summary bar */}
        <div className="shrink-0 border-b border-cream-dark bg-cream px-5 py-4">
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-4">
              <InlineBalanceEdit
                label="Beginning balance"
                value={localBegin}
                onSave={v => handleSaveBalance('beginning', v)}
                isSaving={patchBalances.isPending}
              />
              {live && (
                <div className="flex flex-col">
                  <span className="mb-1 text-xs text-ink-faint">
                    {isCC ? '− Transaction total' : '+ Transaction total'}
                  </span>
                  <span className="text-sm text-ink-soft tabular-nums">{fmt(live.txnSum)}</span>
                </div>
              )}
              {live && (
                <div className="flex flex-col">
                  <span className="mb-1 text-xs text-ink-faint">Expected ending</span>
                  <span className="text-sm text-ink-soft tabular-nums">{fmt(live.expectedEnd)}</span>
                </div>
              )}
              <InlineBalanceEdit
                label="Actual ending"
                value={localEnd}
                onSave={v => handleSaveBalance('ending', v)}
                isSaving={patchBalances.isPending}
              />
            </div>

            {live && (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs font-medium text-ink-faint">Gap:</span>
                <GapDisplay gap={live.gap} large />
                {Math.abs(live.gap) <= 0.50 && (
                  <Tag tone="good" icon={CheckCircle2}>Reconciled</Tag>
                )}
                {Math.abs(live.gap) > 0.50 && Math.abs(live.gap) <= 10 && (
                  <Tag tone="warn" icon={AlertTriangle}>Minor gap</Tag>
                )}
                {Math.abs(live.gap) > 10 && (
                  <Tag tone="danger" icon={XCircle}>Unreconciled</Tag>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Duplicate-transaction warning banner */}
        {hasMixedDates && (
          <div className="flex shrink-0 items-start gap-3 border-b border-clay-200 bg-clay-50 px-5 py-3">
            <AlertTriangle size={16} className="mt-1 shrink-0 text-clay-600" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-clay-800">Possible duplicate transactions detected</p>
              <p className="mt-1 text-xs text-clay-700">
                This statement contains both dated and undated transactions, which can happen when two extraction methods ran and were merged improperly. Removing confirmed duplicates may reduce the gap.
              </p>
            </div>
            <Button
              size="sm"
              variant="primary"
              loading={cleanDuplicates.isPending}
              onClick={handleCleanDuplicates}
              className="shrink-0 bg-clay-600 hover:bg-clay-700"
            >
              {cleanDuplicates.isPending ? 'Cleaning…' : 'Remove Duplicates'}
            </Button>
          </div>
        )}

        {/* Clean result feedback */}
        {cleanResult && (
          <div className="flex shrink-0 items-center gap-2 border-b border-green-200 bg-green-50 px-5 py-2">
            <CheckCircle2 size={16} className="shrink-0 text-green-600" />
            <p className="text-xs text-green-800">
              {cleanResult.deleted > 0
                ? `Removed ${cleanResult.deleted} duplicate transaction${cleanResult.deleted !== 1 ? 's' : ''} — gap updated.`
                : 'No confirmed duplicates found to remove.'}
            </p>
          </div>
        )}

        {/* Mark Balanced button */}
        {canMarkBalanced && !showClosePrompt && (
          <div className="flex shrink-0 items-center gap-3 border-b border-green-200 bg-green-50 px-5 py-3">
            <CheckCircle2 size={16} className="shrink-0 text-green-600" />
            <span className="flex-1 text-sm text-green-800">Gap is within $1.00 — ready to mark as balanced.</span>
            <Button size="sm" onClick={() => setShowClosePrompt(true)}>
              Mark Balanced
            </Button>
          </div>
        )}

        {showClosePrompt && (
          <div className="shrink-0 space-y-2 border-b border-green-200 bg-green-50 px-5 py-3">
            <p className="text-sm font-medium text-green-800">Add a brief note about why this gap is acceptable:</p>
            <Input
              autoFocus
              type="text"
              size="md"
              value={closeNote}
              onChange={e => setCloseNote(e.target.value)}
              placeholder="e.g. rounding — confirmed against paper statement"
              onKeyDown={e => { if (e.key === 'Enter') handleCloseGap(); if (e.key === 'Escape') setShowClosePrompt(false); }}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleCloseGap}
                disabled={!closeNote.trim()}
                loading={closeGap.isPending}
              >
                Confirm
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setShowClosePrompt(false)}>
                Cancel
              </Button>
            </div>
            {closeGap.isError && (
              <p className="mt-1 text-xs text-danger">{(closeGap.error as Error)?.message || 'Could not close gap — please try again.'}</p>
            )}
          </div>
        )}

        {/* Transactions list */}
        <div className="flex-1 overflow-y-auto">
          <div className="sticky top-0 z-(--z-sticky) flex items-center justify-between border-b border-cream-dark bg-cream px-4 py-2">
            <span className="t-eyebrow">
              Transactions ({localTxns.length})
            </span>
            <Button
              size="sm"
              icon={showAddForm ? ChevronUp : Plus}
              onClick={() => setShowAddForm(v => !v)}
            >
              Add transaction
            </Button>
          </div>

          {showAddForm && (
            <div className="shrink-0 space-y-2 border-b border-info bg-info-soft px-4 py-3">
              <p className="text-xs font-medium text-info">Add missing transaction</p>
              <div className="flex flex-wrap gap-2">
                <Input
                  type="date"
                  size="sm"
                  value={addDate}
                  onChange={e => setAddDate(e.target.value)}
                  className="w-36 text-xs"
                />
                <div className="min-w-32 flex-1">
                  <Input
                    type="text"
                    size="sm"
                    placeholder="Description"
                    value={addDesc}
                    onChange={e => setAddDesc(e.target.value)}
                    className="text-xs"
                  />
                </div>
                <div className="flex items-center overflow-hidden rounded border border-border-strong">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAddSign(s => s === '+' ? '-' : '+')}
                    className={`rounded-none border-none px-2 font-bold ${addSign === '-' ? 'bg-danger-soft text-danger hover:bg-danger-soft' : 'bg-green-100 text-green-700 hover:bg-green-100'}`}
                  >
                    {addSign}
                  </Button>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    size="sm"
                    placeholder="0.00"
                    value={addAmt}
                    onChange={e => setAddAmt(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddTxn(); }}
                    className="w-24 border-none px-2 text-right text-xs"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleAddTxn}
                  disabled={!addDate || !addDesc.trim() || !addAmt}
                  loading={addTxn.isPending}
                >
                  Add
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
              </div>
              {addTxn.isError && (
                <p className="text-xs text-danger">{String(addTxn.error)}</p>
              )}
            </div>
          )}

          {txnsLoading ? (
            <div className="px-3 py-2">
              <SkeletonTable rows={6} columns={3} density="compact" />
            </div>
          ) : localTxns.length === 0 ? (
            <EmptyState
              size="inline"
              title="No transactions"
              description="Add one with the button above."
            />
          ) : (
            <div className="divide-y divide-border-subtle">
              <div className="t-eyebrow sticky top-11 flex items-center gap-2 border-b border-cream-dark bg-cream px-3 py-1">
                <span className="w-20 shrink-0">Date</span>
                <span className="flex-1">Description</span>
                <span className="w-28 text-right">Amount</span>
                <span className="w-6" />
              </div>
              {localTxns.map(txn => (
                <TxnRow
                  key={txn.id}
                  txn={txn}
                  onEdit={handleEditTxn}
                  onDelete={handleDeleteTxn}
                  isEditing={editingTxnId === txn.id}
                  isDeleting={deletingTxnId === txn.id}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between border-t border-cream-dark bg-cream px-5 py-3">
          <span className="text-xs text-ink-faint">
            Click any balance or amount to edit. Changes save immediately.
          </span>
          <Button size="sm" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
